import { CheckCircle, ChevronDown, ChevronRight, Clock, FileText, Trash2, TriangleAlert, XCircle } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExtractionJob, JobStatus } from '@/services/aiExtractionService';
import { Button } from '@/components/Common/button';

const MAX_VISIBLE_JOBS = 10;


interface AIExtractionJobListProps {
  jobs: ExtractionJob[];
  selectedJobId: string | null;
  onSelectJob: (jobId: string) => void;
  onDeleteAll?: () => void;
  isLoading?: boolean;
}

const getStatusIcon = (status: JobStatus) => {
  switch (status) {
    case 'pending':
      return <Clock className="h-5 w-5 text-yellow-500" />;
    case 'processing':
      return <Clock className="h-5 w-5 text-blue-500 animate-spin" />;
    case 'review_required':
      return <TriangleAlert className="h-5 w-5 text-orange-500" />;
    case 'completed':
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case 'failed':
      return <XCircle className="h-5 w-5 text-red-500" />;
    case 'cancelled':
      return <XCircle className="h-5 w-5 text-gray-500" />;
    default:
      return <FileText className="h-5 w-5 text-gray-500" />;
  }
};

const getStatusBadge = (status: JobStatus) => {
  const baseClasses = "px-2 py-1 text-xs font-semibold rounded-full";
  switch (status) {
    case 'pending':
      return `${baseClasses} bg-yellow-100 text-yellow-800`;
    case 'processing':
      return `${baseClasses} bg-blue-100 text-blue-800`;
    case 'review_required':
      return `${baseClasses} bg-orange-100 text-orange-800`;
    case 'completed':
      return `${baseClasses} bg-green-100 text-green-800`;
    case 'failed':
      return `${baseClasses} bg-red-100 text-red-800`;
    case 'cancelled':
      return `${baseClasses} bg-gray-100 text-gray-800`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-800`;
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const AIExtractionJobList: React.FC<AIExtractionJobListProps> = ({
  jobs,
  selectedJobId,
  onSelectJob,
  onDeleteAll,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(true);

  if (isLoading && jobs.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <p className="text-center text-muted-foreground">{t('aiExtractionJobList.loadingJobs')}</p>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-6">
        <p className="text-center text-muted-foreground">{t('aiExtractionJobList.noJobs')}</p>
      </div>
    );
  }

  const visibleJobs = jobs.slice(0, MAX_VISIBLE_JOBS);
  const hiddenCount = Math.max(0, jobs.length - visibleJobs.length);

  return (
    <div className="rounded-xl border border-border bg-card" data-tour="genie-job-list">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className={`w-full px-6 py-4 flex items-center justify-between text-left hover:bg-accent/30 transition-colors ${
          collapsed ? '' : 'border-b border-border'
        }`}
        aria-expanded={!collapsed}
      >
        <div className="flex items-center gap-2">
          {collapsed ? (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
          <h3 className="text-lg font-semibold text-foreground">
            {t('aiExtractionJobList.title')}
            <span className="ml-2 text-sm font-normal text-muted-foreground">({jobs.length})</span>
          </h3>
        </div>
        {onDeleteAll && jobs.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteAll();
            }}
            className="text-destructive hover:text-destructive/80"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            {t('aiExtractionJobList.clearHistory')}
          </Button>
        )}
      </button>
      {!collapsed && (
      <div className="divide-y divide-border">
        {visibleJobs.map((job, index) => (
          <div
            key={job.id}
            className={`p-4 hover:bg-accent/50 cursor-pointer transition-colors min-w-0 overflow-hidden ${
              selectedJobId === job.id ? 'bg-accent border-l-4 border-primary' : ''
            }`}
            onClick={() => onSelectJob(job.id)}
            data-tour={index === 0 ? "genie-job-item" : undefined}
          >
            <div className="flex items-start justify-between min-w-0">
              <div className="flex items-start space-x-3 flex-1 min-w-0">
                {getStatusIcon(job.status)}
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex items-center space-x-2 mb-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate min-w-0">
                      {job.document_name}
                    </p>
                    <span className={`flex-shrink-0 ${getStatusBadge(job.status)}`} data-tour={index === 0 ? "genie-job-status" : undefined}>
                      {job.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>{formatDate(job.created_at)}</span>
                    {job.document_size_kb && (
                      <span>{job.document_size_kb} KB</span>
                    )}
                    {job.requested_action !== 'auto' && (
                      <span className="capitalize">{job.requested_action.replace('_', ' ')}</span>
                    )}
                  </div>
                  {job.error_message && (
                    <p className="mt-1 text-xs text-destructive break-words break-all">
                      {t('aiExtractionJobList.errorLabel')} {job.error_message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {hiddenCount > 0 && (
          <div className="px-6 py-3 text-center text-xs text-muted-foreground">
            {t('aiExtractionJobList.moreHidden', {
              count: hiddenCount,
              defaultValue: '+ {{count}} older job(s) hidden',
            })}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default AIExtractionJobList;

